import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ResponsiveContainer,
    Cell,
    Legend,
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    Radar,
    PieChart,
    Pie,
    LineChart,
    Line,
    AreaChart,
    Area,
    ScatterChart,
    Scatter,
    Sector,
} from "recharts";
import { skills } from "../../data/skills";
import { useState } from "react";

export const colors = [
    "#8A56B8",
    "#FD5396",
    "#F9DE64",
    "#FA5A45",
    "#D5363D",
    "#95DDDF",
    "#565484",
    "#FF7D66",
    "#FFE887",
    "#222666",
    "#FF785B",
    "#ECD38B",
    "#E3B270",
    "#6C6761",
    "#B21C6C",
    "#F9B861",
    // "#FBF7EC",
    // "#2C855D",
    // "#303A6B",
    // "#554C5E",sss
    "#9F897C",
    "#E3B797",
    "#ECE8CF",
    "#619A8A",
    "#EBE4C3",
    "#73BDB0",
    "#396192",
    "#B86969",
    "#E69F67",
    "#F28F77",
    "#FFC375",
    "#55548F",
    "#53A3BD",
    "#B9E69E",
    "#FF8282",
    "#FFECA1",
    "#FF8CA8",
    "#513E7E",
    "#3F9BB0",
    "#8DDADD",
    "#7AB2DD",
    "#7E87BF",
    "#DDCAB1",
    "#F5EDD3",
];

/* ================= CUSTOM TOOLTIP ================= */
const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div
                style={{
                    background: "#111",
                    padding: "10px 14px",
                    borderRadius: "8px",
                    color: "#fff",
                    boxShadow: "0 0 10px rgba(0,0,0,0.4)",
                }}
            >

                <div>Level: <strong>{label}</strong> {payload[0].value}%</div>
            </div>
        );
    }
    return null;
};

export const SkillBarChart = () => (
    <ResponsiveContainer width="100%" height={400}>
        <BarChart
            data={skills}
            margin={{ top: 20, right: 20, left: 10, bottom: 80 }}
        >
            <defs>
                <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#8884d8" />
                    <stop offset="100%" stopColor="#82ca9d" />
                </linearGradient>
            </defs>

            <CartesianGrid strokeDasharray="3 3" />

            <XAxis
                dataKey="name"
                interval="preserveStartEnd"
                angle={-35}
                textAnchor="end"
                height={80}
                tick={{ fontSize: 12 }}
                tickMargin={10}
            />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Legend />

            <Bar
                dataKey="level"
                fill="url(#barGradient)"
                radius={[6, 6, 0, 0]}
                animationDuration={1200}
                activeBar={{ fill: "#ff8042" }}
                onClick={(data) =>
                    alert(`Skill: ${data.name}\nLevel: ${data.level}%`)
                }
            >
                {skills.map((entry, index) => (
                    <Cell key={index} fill={colors[index % colors.length]} />
                ))}
            </Bar>
        </BarChart>
    </ResponsiveContainer>
);

/* ================= RADAR CHART ================= */
export const SkillRadarChart = () => (
    <ResponsiveContainer width="100%" height={350}>
        <RadarChart data={skills}>
            <PolarGrid />
            <PolarAngleAxis dataKey="name" />
            <PolarRadiusAxis />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Radar
                dataKey="level"
                stroke="#ff8042"
                fill="#F28F77"
                fillOpacity={0.5}
                dot={{ r: 4 }}
                animationDuration={1200}
            />
        </RadarChart>
    </ResponsiveContainer>
);

export const SkillPieChart = () => {
    const [activeIndex, setActiveIndex] = useState(0);

    const renderActiveShape = (props) => {
        const {
            cx,
            cy,
            innerRadius,
            outerRadius,
            startAngle,
            endAngle,
            fill,
        } = props;

        return (
            <Sector
                cx={cx}
                cy={cy}
                innerRadius={innerRadius}
                outerRadius={outerRadius + 10}
                startAngle={startAngle}
                endAngle={endAngle}
                fill={fill}
            />
        );
    };

    return (
        <ResponsiveContainer width="100%" height={350}>
            <PieChart>
                <Tooltip content={<CustomTooltip />} />
                <Legend />

                <Pie
                    data={skills}
                    dataKey="level"
                    nameKey="name"
                    innerRadius={70}
                    outerRadius={120}
                    paddingAngle={3}
                    activeIndex={activeIndex}
                    activeShape={renderActiveShape}
                    onMouseEnter={(_, index) => setActiveIndex(index)}
                    animationDuration={1200}
                    label
                >
                    {skills.map((entry, index) => (
                        <Cell key={index} fill={colors[index % colors.length]} />
                    ))}
                </Pie>
            </PieChart>
        </ResponsiveContainer>
    );
};

/* ================= LINE CHART ================= */
export const SkillLineChart = () => (
    <ResponsiveContainer width="100%" height={350}>
        <LineChart data={skills}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
                dataKey="name"
                interval="preserveStartEnd"
                angle={-35}
                textAnchor="end"
                height={80}
                tick={{ fontSize: 12 }}
            />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line
                type="monotone"
                dataKey="level"
                stroke="#00C49F"
                strokeWidth={4}
                dot={{ r: 6 }}
                activeDot={{ r: 9 }}
                animationDuration={1200}
            />
        </LineChart>
    </ResponsiveContainer>
);

/* ================= AREA CHART ================= */
export const SkillAreaChart = () => (
    <ResponsiveContainer width="100%" height={350}>
        <AreaChart data={skills}>
            <defs>
                <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#82ca9d" stopOpacity={0.8} />
                    <stop offset="100%" stopColor="#82ca9d" stopOpacity={0.2} />
                </linearGradient>
            </defs>

            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
                dataKey="name"
                interval="preserveStartEnd"
                angle={-35}
                textAnchor="end"
                height={80}
                tick={{ fontSize: 12 }}
            />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Area
                type="monotone"
                dataKey="level"
                stroke="#82ca9d"
                fill="url(#areaGradient)"
                animationDuration={1200}
            />
        </AreaChart>
    </ResponsiveContainer>
);

/* ================= BUBBLE / SCATTER CHART ================= */
export const SkillBubbleChart = () => (
    <ResponsiveContainer width="100%" height={350}>
        <ScatterChart>
            <CartesianGrid />
            <XAxis
                dataKey="name"
                scale="point"
                interval="preserveStartEnd"
                angle={-35}
                textAnchor="end"
                height={80}
                tick={{ fontSize: 12 }}
                type="category"
            />
            <YAxis dataKey="level" />
            <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: "3 3" }} />
            <Scatter data={skills} fill="#8884d8" />
        </ScatterChart>
    </ResponsiveContainer>
);